import { Component, OnInit, AfterViewInit, ViewChild, OnDestroy } from "@angular/core";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { TechnicalLetter } from "../interfaces/technical-letters/technical-letters.models";
import { TechnicalLetterService } from "../services/technical-letters/technical-letter.service";
import { finalize, debounceTime, distinctUntilChanged } from "rxjs/operators";
import {
  GridColumn,
  GridAction,
  GridConfig,
} from "../shared/data-grid/data-grid.component";
import * as ExcelJS from "exceljs";
import { lastValueFrom, Subject } from "rxjs";

import { Router } from "@angular/router";

interface ColumnDef {
  key: string;
  label: string;
  filterable?: boolean;
}

@Component({
    selector: "app-technical-letters",
    templateUrl: "./technical-letters.component.html",
    styleUrls: ["./technical-letters.component.css"],
    standalone: false
})
export class TechnicalLettersComponent implements OnInit, AfterViewInit, OnDestroy {
  data: TechnicalLetter[] = [];
  displayedColumns: ColumnDef[] = [
    { key: "issuer", label: "Issuer", filterable: true },
    { key: "letters", label: "Letters", filterable: true },
    { key: "letterTitle", label: "Letter Title", filterable: true },
    { key: "issueDate", label: "Issue Date", filterable: true },
    { key: "lastModifier", label: "Last Modifier", filterable: true },
    { key: "category", label: "Category", filterable: true },
    { key: "businessUnit", label: "Business Unit", filterable: true },
    { key: "plant", label: "Plant", filterable: true },
    { key: "equipmentName", label: "Equipment Name", filterable: true },
    { key: "disposition", label: "Disposition", filterable: true },
    { key: "doc", label: "Doc", filterable: false },
    { key: "edit", label: "Edit", filterable: false },
  ];
  displayedColumnsKeys = this.displayedColumns.map((col) => col.key);

  loading = false;
  initialLoading = true; // For initial page load
  isFiltering = false;   // For search/filter operations
  error: string | null = null;
  totalCount = 0;
  pageNumber = 1;
  pageSize = 10;
  searchText: string = "";
  sortColumn: string = "";
  sortDirection: string = "";
  filters: any = {};

  isExporting = false;
  exportProgress = 0;

  private searchSubject = new Subject<string>();
  private filterSubject = new Subject<string>();
 
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    private router: Router,
    private technicalLetterService: TechnicalLetterService
  ) {}

  ngOnInit(): void {
    // Setup debounced search for main search box
    this.searchSubject.pipe(
      debounceTime(300) // Reduced debounce time for better responsiveness
    ).subscribe(() => {
      this.applyFilters();
    });

    // Setup debounced search for column filters
    this.filterSubject.pipe(
      debounceTime(300)
    ).subscribe(() => {
      this.applyFilters();
    });

    this.loadTechnicalLetters();
  }

  ngOnDestroy(): void {
    this.searchSubject.complete();
    this.filterSubject.complete();
  }

  ngAfterViewInit() {
    // If using MatTableDataSource, set paginator and sort here
    // Not needed for manual data array + server paging
  }

  applyFilters() {
    this.pageNumber = 1; 
    this.loadTechnicalLetters(
      this.pageNumber,
      this.pageSize,
      this.filters,
      this.searchText,
      this.sortColumn,
      this.sortDirection,
      false, // Don't show main loading spinner for filtering
      true   // Show filtering indicators
    );
  }

  onSearchInput() {
    this.searchSubject.next(`${this.searchText}:${Date.now()}`);
  }

  onFilterInput(columnKey: string, value: string) {
    this.filters[columnKey] = value;
    this.filterSubject.next(`${columnKey}:${value}:${Date.now()}`);
  }

  onPageChange(event: any) {
    this.pageNumber = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.loadTechnicalLetters(
      this.pageNumber,
      this.pageSize,
      this.filters,
      this.searchText,
      this.sortColumn,
      this.sortDirection,
      false, // Don't show full loading for pagination
      false  // Not a filter operation
    );
  }
  onDocClick(row: any) {
    if (row.documentUrl) {
      window.open(row.documentUrl, "_blank");
    }
  }

  loadTechnicalLetters(
    pageNumber = 1,
    pageSize = 10,
    filters: any = {},
    searchText: string = "",
    sortColumn: string = "",
    sortDirection: string = "",
    showLoading: boolean = true,
    isFilterOperation: boolean = false
  ): void {
    if (showLoading) {
      this.loading = true;
      this.initialLoading = true;
    }
    
    if (isFilterOperation) {
      this.isFiltering = true;
    }
    
    this.error = null;
    this.technicalLetterService
      .getTechnicalLettersPaged(
        pageNumber,
        pageSize,
        filters,
        searchText,
        sortColumn,
        sortDirection
      )
      .pipe(finalize(() => {
        this.loading = false;
        this.initialLoading = false;
        this.isFiltering = false;
      }))
      .subscribe({
        next: (response) => {
          this.data = (response.data || []).map((item: any) => ({
            id: item.letterId,
            equipmentId: item.equipmentId,
            issuer: item.letterIssuer,
            letters: item.letterNumber,
            letterTitle: item.letterTitle,
            issueDate: item.letterIssueDate?.split("T")[0],
            lastModifier: item.rowUpdatedDate?.split("T")[0],
            category: item.letterCategory,
            businessUnit: item.businessUnit,
            plant: item.plant,
            equipmentName: item.equipment,
            disposition: item.disposition,
            doc: "",
            documentUrl: item.documentUrl,
          }));
          this.totalCount = response.totalCount || 0;
        },
        error: (error) => {
          this.error = "Failed to load service bulletins. Please try again.";
          this.data = []; 
          this.totalCount = 0;
          console.error("Error loading service bulletins:", error);
        },
      });
  }
  onSortChange(event: any) {
    this.sortColumn = event.active;
    this.sortDirection = event.direction;
    this.loadTechnicalLetters(
      this.pageNumber,
      this.pageSize,
      this.filters,
      this.searchText,
      this.sortColumn,
      this.sortDirection,
      false, // Don't show full loading for sorting
      true   // This is a filter-like operation
    );
  }

  plantAdmins() {
    this.router.navigate(["/technical-letters/plant-admins"]);
  }

  DeletedLetterLogs() {
    this.router.navigate(["/technical-letters/deleted-letter-logs"]);
  }

  editLetter(letter: TechnicalLetter) {
    const urlTree = this.router.createUrlTree([
      "/technical-letters/edit-technical-letters",
      letter.id,
    ], {
      queryParams: { equipmentId: letter.equipmentId }
    });
    const url = window.location.origin + this.router.serializeUrl(urlTree);
    const newWindow = window.open(url, "_blank", "noopener,noreferrer");
    if (newWindow) {
      newWindow.focus();
    }
  }
  
  async exportToExcel() {
    this.isExporting = true;
    this.exportProgress = 0;
    const pageSize = 500;
    let page = 1;
    let allData: any[] = [];
    const headerRow = [
      "Letter_ID",
      "LETTER_EQUIP_ID",
      "EQUIPMENT_ID",
      "LETTER_ISSUER",
      "LETTER_NUMBER",
      "LETTER_TITLE",
      "RECURRING_FLAG",
    ];
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("TIL Search Extract");
    worksheet.addRow(headerRow);
    worksheet.getRow(1).font = {
      bold: true,
      size: 12,
      color: { argb: "000000" },
    };
    worksheet.getRow(1).alignment = {
      horizontal: "center",
      vertical: "middle",
    };
    let rowsAdded = 0;
    while (rowsAdded < this.totalCount) {
      var response = await lastValueFrom(
        this.technicalLetterService.getTechnicalLettersPaged(page, pageSize)
      );
      this.exportProgress = Math.min(
        10 + (page * 90) / Math.ceil(this.totalCount / pageSize),
        100
      );
      allData = response.data;
      const rows = allData.map((item) => [
        item.letterId,
        item.letterEquipId,
        item.equipmentId,
        item.letterIssuer,
        item.letterNumber,
        item.letterTitle,
        item.recurringFlag,
      ]);
      rowsAdded += rows.length;
      worksheet.addRows(rows);
      worksheet.eachRow((row, _) => {
        row.eachCell((cell, colNumber) => {
          cell.alignment = {
            horizontal: "center",
            vertical: "middle",
            wrapText: colNumber === 6,
          };
        });
      });
      page++;
    }
    worksheet.getColumn("A").width = 30;
    worksheet.getColumn("B").width = 30;
    worksheet.getColumn("C").width = 30;
    worksheet.getColumn("D").width = 30;
    worksheet.getColumn("E").width = 30;
    worksheet.getColumn("F").width = 30;
    worksheet.getColumn("G").width = 30;
    worksheet.workbook.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([buffer], { type: "application/octet-stream" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "TIL Search Extract.xlsx";
      a.click();
      window.URL.revokeObjectURL(url);
    });
    this.isExporting = false;
  }
}